import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import { apiBase } from '../../utils/config';
import CommonConfig from '../../utils/constant';
import Toast from '../Shared/Toast/Toast';
import { useCatchResponse } from '../../utils/useCatchResponse';
import { useNavigate } from 'react-router-dom';
import useSocket from '../../utils/useSocket';
import moment from 'moment';
import './NotificationList.css';

const typeOptions = [
    { label: 'All', value: 'ALL' },
    { label: 'General', value: 'GENERAL' },
    { label: 'Attendance', value: 'ATTENDANCE' },
    { label: 'Fees', value: 'FEES' },
    { label: 'Exam', value: 'EXAM' },
    { label: 'Result', value: 'RESULT' },
    { label: 'Event', value: 'EVENT' },
    { label: 'Hall Ticket', value: 'HALLTICKET' },
];

const typeColors = {
    GENERAL: { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32' },
    ATTENDANCE: { bg: '#e3f2fd', border: '#2196f3', text: '#1565c0' },
    FEES: { bg: '#fff3e0', border: '#ff9800', text: '#e65100' },
    EXAM: { bg: '#f3e5f5', border: '#9c27b0', text: '#6a1b9a' },
    RESULT: { bg: '#e0f2f1', border: '#009688', text: '#00695c' },
    EVENT: { bg: '#fce4ec', border: '#e91e63', text: '#880e4f' },
    HALLTICKET: { bg: '#e8eaf6', border: '#3f51b5', text: '#283593' },
};

const NotificationList = () => {
    const { catchResponse } = useCatchResponse();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [securityUserID, setSecurityUserID] = useState('');
    const [searchText, setSearchText] = useState('');
    const [showFilter, setShowFilter] = useState(false);
    const [selectedType, setSelectedType] = useState('ALL');
    const [fromDate, setFromDate] = useState(moment().subtract(30, 'days').toDate());
    const [toDate, setToDate] = useState(new Date());
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [unreadCount, setUnreadCount] = useState(0);
    // Access from the "Notifications" menu — View gates the page, Delete the clear action.
    const notifAccess = CommonConfig.getUserAccess('Notifications') || CommonConfig.getUserAccess('Notification') || {};
    const canView = !!notifAccess?.ViewAccess;
    const canDelete = !!notifAccess?.DeleteAccess;
    const limit = 20;

    useEffect(() => {
        const loginData = CommonConfig.loginData();
        setSecurityUserID(loginData?._id);
        if (!canView) { setLoading(false); return; }
        if (loginData?._id) fetchNotifications(loginData._id);
    }, []);

    useSocket(null, () => {
        if (!canView) return;
        const id = securityUserID || CommonConfig.loginData()?._id;
        if (id) fetchNotifications(id, 1);
    });

    const fetchNotifications = async (userId, pageNo = 1, type = selectedType, from = fromDate, to = toDate) => {
        try {
            setLoading(true);
            const response = await api.post(APIConstant.path.GetNotificationsList, {
                UserId: userId || securityUserID,
                Type: type || 'ALL',
                Page: pageNo,
                Limit: limit,
                FromDate: from ? moment(from).format('YYYY-MM-DD') : undefined,
                ToDate: to ? moment(to).format('YYYY-MM-DD') : undefined,
            });
            if (response.success) {
                setNotifications(pageNo === 1 ? (response.data || []) : [...notifications, ...(response.data || [])]);
                setTotalCount(response.total || 0);
                setUnreadCount(response.unreadCount || 0);
                setPage(pageNo);
            }
            setLoading(false);
            Loader.hide();
        } catch (err) {
            setLoading(false);
            Loader.hide();
            catchResponse(err);
        }
    };

    const handleMarkAsRead = async (id) => {
        try {
            await api.post(APIConstant.path.MarkNotificationAsRead, { UserId: securityUserID, NotificationId: id });
            setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) { console.log(err); }
    };

    const handleMarkAllRead = async () => {
        try {
            await api.post(APIConstant.path.MarkAllNotificationsAsRead, { UserId: securityUserID });
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
            Toast.success({ message: 'All notifications marked as read' });
        } catch (err) { catchResponse(err); }
    };

    const handleDelete = async (id) => {
        try {
            await api.post(APIConstant.path.DeleteNotification, { UserId: securityUserID, NotificationId: id });
            setNotifications(prev => prev.filter(n => n._id !== id));
            Toast.success({ message: 'Notification deleted' });
        } catch (err) { catchResponse(err); }
    };

    const handleNotificationClick = (n) => {
        if (!n.isRead) handleMarkAsRead(n._id);
        if (n.redirectUrl) navigate(n.redirectUrl, { state: { examName: n.data?.examName } });
    };

    const applyFilter = () => {
        setShowFilter(false);
        fetchNotifications(securityUserID, 1, selectedType, fromDate, toDate);
    };

    const loadMore = () => {
        fetchNotifications(securityUserID, page + 1, selectedType, fromDate, toDate);
    };

    // Group notifications by date
    const groupedNotifications = {};
    notifications.forEach(n => {
        const dateKey = moment(n.createdAt).format('DD/MM/YYYY');
        if (!groupedNotifications[dateKey]) groupedNotifications[dateKey] = [];
        groupedNotifications[dateKey].push(n);
    });

    if (!canView) {
        return (
            <div className='wayment-owner-dashboard et-master-page'>
                <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>
                    <i className="pi pi-lock" style={{ fontSize: '3rem', display: 'block', marginBottom: '12px' }}></i>
                    You don't have access to view notifications.
                </div>
            </div>
        );
    }

    return (
        <div className='wayment-owner-dashboard et-master-page'>
            <div className="heading-area p-d-flex p-ai-center p-jc-between">
                <div className="p-d-flex p-ai-baseline">
                    <i className="pi pi-bell"></i>
                    <h2>Notifications</h2>
                    {unreadCount > 0 && (
                        <Tag value={`${unreadCount} unread`} severity="danger" className="p-ml-2" />
                    )}
                </div>
                <div className="p-d-flex p-ai-center" style={{ gap: '8px' }}>
                    {unreadCount > 0 && (
                        <Button icon="pi pi-check-circle" label="Mark all read" className="e-h-btn" onClick={handleMarkAllRead} />
                    )}
                    <Button icon="pi pi-filter" tooltip="Filter" tooltipOptions={{ position: 'bottom' }} className="e-h-btn" onClick={() => setShowFilter(true)} style={{ height: '36px', width: '36px' }} />
                </div>
            </div>

            <div className="notif-date-range p-mt-3">
                {moment(fromDate).format('DD/MM/YYYY')} - {moment(toDate).format('DD/MM/YYYY')}
            </div>

            <div className="notif-list-container">
                {loading && notifications.length === 0 ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 300px)' }}>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#1C515E' }}></i>
                    </div>
                ) : notifications.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 200px)', color: '#999' }}>
                        <i className="pi pi-bell-slash" style={{ fontSize: '3rem', marginBottom: '12px' }}></i>
                        <span style={{ fontSize: '1.1rem', fontWeight: '500' }}>No notifications found</span>
                    </div>
                ) : (
                    <>
                        {Object.entries(groupedNotifications).map(([date, items]) => (
                            <div key={date}>
                                <div className="notif-date-header">{date}</div>
                                {items.map(n => {
                                    const colors = typeColors[n.type] || typeColors.GENERAL;
                                    return (
                                        <div
                                            key={n._id}
                                            className={`notif-card ${!n.isRead ? 'unread' : ''}`}
                                            style={{ borderLeftColor: colors.border }}
                                            onClick={() => handleNotificationClick(n)}
                                        >
                                            <div className="notif-card-body" style={{ backgroundColor: colors.bg }}>
                                                <div className="notif-card-header">
                                                    <span className="notif-title" style={{ color: colors.text }}>{n.title}</span>
                                                    <span className="notif-time">{moment(n.createdAt).format('hh:mm A')}</span>
                                                </div>
                                                <div className="notif-message">{n.message}</div>
                                                {Array.isArray(n.data?.attachments) && n.data.attachments.length > 0 && (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '8px 0' }}>
                                                        {n.data.attachments.map((file, i) => {
                                                            const url = `${apiBase}/Upload/Announcement/${file}?token=${CommonConfig.fileToken()}`;
                                                            const downloadUrl = `${apiBase}/Upload/Announcement/download/${file}?token=${CommonConfig.fileToken()}`;
                                                            const name = String(file).replace(/^\d+-/, '');
                                                            const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file);
                                                            return (
                                                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                    <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} title={name}>
                                                                        {isImage
                                                                            ? <img src={url} alt={name} style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e0e0e0' }} />
                                                                            : <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#2f6fed', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '4px 8px' }}><i className="pi pi-file"></i>{name}</span>}
                                                                    </a>
                                                                    <a href={downloadUrl} onClick={(e) => e.stopPropagation()} title={`Download ${name}`} style={{ color: '#2f6fed', padding: '2px' }}>
                                                                        <i className="pi pi-download" style={{ fontSize: '13px' }}></i>
                                                                    </a>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                <div className="notif-card-footer">
                                                    <div className="p-d-flex p-ai-center" style={{ gap: '8px' }}>
                                                        <Tag value={n.type} style={{ background: colors.border, fontSize: '10px' }} />
                                                        {n.redirectUrl && (
                                                            <span className="notif-more" style={{ color: colors.text }}>More</span>
                                                        )}
                                                    </div>
                                                    <div className="notif-card-actions">
                                                        {!n.isRead && (
                                                            <Button
                                                                icon="pi pi-check"
                                                                tooltip="Mark as read"
                                                                tooltipOptions={{ position: 'top' }}
                                                                className="p-button-rounded p-button-text p-button-sm notif-action-btn"
                                                                onClick={(e) => { e.stopPropagation(); handleMarkAsRead(n._id); }}
                                                            />
                                                        )}
                                                        {canDelete && (
                                                            <Button
                                                                icon="pi pi-trash"
                                                                tooltip="Delete"
                                                                tooltipOptions={{ position: 'top' }}
                                                                className="p-button-rounded p-button-text p-button-sm p-button-danger notif-action-btn"
                                                                onClick={(e) => { e.stopPropagation(); handleDelete(n._id); }}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                        {notifications.length < totalCount && (
                            <div style={{ textAlign: 'center', padding: '16px' }}>
                                <Button label="Load More" className="p-button-sm p-button-outlined" onClick={loadMore} loading={loading} />
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Filter Dialog */}
            <Dialog
                visible={showFilter}
                onHide={() => setShowFilter(false)}
                header="Filter List"
                style={{ width: '30vw' }}
                draggable={false}
                dismissableMask
            >
                <div className="p-grid">
                    <div className="p-col-12 p-mb-3 ">
                        <div className="custom-inputs">
                            <label>Event Type</label>
                            <Dropdown
                                className="p-w-100 p-mt-1"
                                value={selectedType}
                                options={typeOptions}
                                onChange={(e) => setSelectedType(e.value)}
                            />
                        </div>
                    </div>
                    <div className="p-col-12 p-mb-3">
                        <div className="custom-inputs">
                            <label >From Date</label>
                            <Calendar
                                className="p-w-100 p-mt-1"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                            />
                        </div>
                    </div>
                    <div className="p-col-12 p-mb-3">
                        <div className="custom-inputs">
                            <label style={{ fontWeight: 600, fontSize: '13px' }}>To Date</label>
                            <Calendar
                                className="p-w-100 p-mt-1"
                                value={toDate}
                                onChange={(e) => setToDate(e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                minDate={fromDate}
                            />
                        </div>
                    </div>
                    <div className="p-col-12 p-d-flex p-jc-end">
                        <Button label="Cancel" className='p-m-0 btn-text-action-secondary p-m-1' onClick={() => setShowFilter(false)} />
                        <Button label="Apply" className='p-m-0 btn-text-action-primary p-m-1 color-white submit-focus' onClick={applyFilter} />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default NotificationList;
